import { BRAND, USER } from '@/models/auth/permissions.enum';
import { getChannelText, getChannelConfigProp } from '@/models/dashboards/channels.enum';
import {
  CHANNELS,
  DATA_SOURCES,
  FRACTIONAL_METRIC_TYPES,
  METRIC_TYPE_TO_DASHING_UI_FORMAT,
} from '@/app/campaigns/constants';
import { CHART } from '@/ux/colours';
import enumTypes, { sourceTypeEnum } from '@/app/library/constants';
import { formatDateRangeLabel } from '@/utils/formatters';
import { formatInTimeZone } from 'date-fns-tz';
import { getUserTimezone } from '@/utils/timezone';
import dayjs from 'dayjs';
import { GRAPH_SCALES } from '@/models/dashboards/graph-scales.enum';
import { Formatters } from '@dashhudson/dashing-ui';
import { checkTweetMediaIsOriginal } from '@/utils/media';
import { mediaSourceType } from '@/config';
import { getHandle } from '@/app/creators/utils';
import { toValue } from 'vue';
import isEmpty from 'lodash/isEmpty';
/**
 * Returns an object checking whether the brand and user have access to a module.
 * @param {function} [guard] - Function that checks permission for a module.
 */
export const getPermissionsMap = (guard) => ({
  TIKTOK:
    guard(USER.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS) && guard(BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS),
  INSTAGRAM:
    guard(USER.INSTAGRAM.CAN_ACCESS_INSTAGRAM) && guard(BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM),
  INSTAGRAM_STORY:
    guard(USER.INSTAGRAM.CAN_ACCESS_INSTAGRAM) && guard(BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM),
  FACEBOOK:
    guard(USER.FACEBOOK.CAN_ACCESS_FB_INSIGHTS) && guard(BRAND.FACEBOOK.CAN_ACCESS_FB_INSIGHTS),
  PINTEREST: guard(USER.PINTEREST.CAN_ACCESS_PINT) && guard(BRAND.PINTEREST.CAN_ACCESS_PINT),
  TWITTER:
    guard(USER.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS) &&
    guard(BRAND.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS),
  YOUTUBE:
    guard(USER.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS) &&
    guard(BRAND.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS),
  UGC: guard(USER.INSTAGRAM.CAN_ACCESS_UGC_MORE) && guard(BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE),
  RELATIONSHIPS:
    guard(USER.INSTAGRAM.CAN_ACCESS_RELATIONSHIPS) &&
    guard(BRAND.INSTAGRAM.CAN_ACCESS_RELATIONSHIPS),
  GOOGLE_ANALYTICS:
    guard(USER.CAMPAIGNS.CAN_ACCESS_CAMPAIGNS_GOOGLE_ANALYTICS) &&
    guard(BRAND.CAMPAIGNS.CAN_ACCESS_CAMPAIGNS_GOOGLE_ANALYTICS),
  CREATORS: guard(USER.CREATORS.CAN_ACCESS_CREATORS) && guard(BRAND.CREATORS.CAN_ACCESS_CREATORS),
  SENTIMENT: guard(BRAND.GENERAL.CAN_ACCESS_SENTIMENT),
  CAMPAIGNS:
    guard(USER.CAMPAIGNS.CAN_ACCESS_CAMPAIGNS) && guard(BRAND.CAMPAIGNS.CAN_ACCESS_CAMPAIGNS),
});

/**
 * Return the formatted label for the provided channel.
 * @param {string} channel - The key for the channel (e.g. TIKTOK).
 */
export const getChannelLabel = (channel) => {
  switch (channel) {
    case CHANNELS.RELATIONSHIPS:
      return 'Relationships';
    case CHANNELS.UGC:
      return 'UGC';
    default:
      return getChannelText(channel);
  }
};

const VALID_CAMPAIGN_MEDIA_SOURCES = [
  mediaSourceType.INSTAGRAM,
  mediaSourceType.INSTAGRAM_STORY,
  mediaSourceType.FACEBOOK,
  mediaSourceType.FACEBOOK_LINK,
  mediaSourceType.FACEBOOK_TEXT,
  mediaSourceType.FACEBOOK_TEXT_LINK,
  mediaSourceType.TIKTOK,
  mediaSourceType.YOUTUBE,
  mediaSourceType.PINTEREST,
  mediaSourceType.TWITTER,
  mediaSourceType.TWITTER_LINK,
  mediaSourceType.TWITTER_TEXT,
];

const VALID_CAMPAIGN_BRAND_MEDIA_TYPES = [
  enumTypes.FACEBOOK_OWNED,
  enumTypes.FACEBOOK_LINK,
  enumTypes.FACEBOOK_TEXT_LINK,
  enumTypes.FACEBOOK_TEXT,
  enumTypes.INSTAGRAM_OWNED,
  enumTypes.INSTAGRAM_OWNED_IGTV,
  enumTypes.INSTAGRAM_STORY,
  enumTypes.PINTEREST_OWNED,
  enumTypes.TIKTOK_OWNED,
  enumTypes.TWITTER_OWNED,
  enumTypes.TWITTER_OWNED_LINK,
  enumTypes.TWITTER_OWNED_TEXT,
  enumTypes.YOUTUBE_OWNED,
  enumTypes.INSTAGRAM_UGC,
  enumTypes.INSTAGRAM_UGC_IGTV,
  enumTypes.INSTAGRAM_STORY_UGC,
];

/**
 * Return whether a media item is eligible to be added to Campaigns.
 * @param {object} media - media item (camelized keys)
 * @returns {boolean}
 */
export function isMediaEligibleForCampaign(media) {
  if (!media) return false;
  // In the media drawer, the `sourceType` will correspond to the BrandMedia type (e.g INSTAGRAM_OWNED, FACEBOOK_OWNED, etc.)
  // On pages with using a media/v2 style response, `sourceType` is something like 'OWNED' or 'UGC'
  // The `source` will be the media source (e.g. INSTAGRAM, FACEBOOK, etc.)
  const { source, sourceType } = media || {};

  // Special case for X media: only allow owned, original tweets (no retweets, replies, etc.)
  if (
    [mediaSourceType.TWITTER, mediaSourceType.TWITTER_LINK, mediaSourceType.TWITTER_TEXT].includes(
      source,
    )
  ) {
    const isOwnedTweet =
      sourceType === sourceTypeEnum.OWNED ||
      [
        enumTypes.TWITTER_OWNED,
        enumTypes.TWITTER_OWNED_LINK,
        enumTypes.TWITTER_OWNED_TEXT,
      ].includes(sourceType);

    return isOwnedTweet && checkTweetMediaIsOriginal(media);
  }

  if (VALID_CAMPAIGN_BRAND_MEDIA_TYPES.includes(sourceType)) return true;

  if (VALID_CAMPAIGN_MEDIA_SOURCES.includes(source)) {
    if (sourceType === sourceTypeEnum.OWNED) return true;

    // Only Instagram UGC is eligible for a campaign
    if (sourceType === sourceTypeEnum.UGC) {
      return source === enumTypes.INSTAGRAM || source === mediaSourceType.INSTAGRAM_STORY;
    }
    return false;
  }

  return false;
}

/**
 * Return the chart color for the provided channel.
 * @param {string} channel - The key for the channel (e.g. TIKTOK).
 */
export const getChannelChartColor = (channel) => {
  switch (channel) {
    case CHANNELS.RELATIONSHIPS:
      return CHART.SET.CHART_SET_16;
    case CHANNELS.UGC:
      return CHART.SET.CHART_SET_11;
    default:
      return getChannelConfigProp(channel, 'chartColor');
  }
};

/**
 * Return sourceType value from media item object.
 */
export function getSourceType(item) {
  return item?.source_type ?? item?.sourceType ?? '';
}

/**
 * Return a formatted date range for the creators date range.
 * @param {date} sourceCreatedAt - The date object for the creators date range.
 */
export function getCreatorsDateRange(sourceCreatedAt) {
  if (!sourceCreatedAt) return 'All Time';

  const dateFormat = 'yyyy-MM-dd';
  const timeZone = getUserTimezone();

  const startDateString = formatInTimeZone(sourceCreatedAt.start, timeZone, dateFormat);
  const endDateString = formatInTimeZone(sourceCreatedAt.end, timeZone, dateFormat);

  return formatDateRangeLabel(startDateString, endDateString);
}

/**
 * Return a status of a campaign based on creators date range.
 * @param {date} sourceCreatedAt - The date object for the creators date range.
 */
export function getCampaignStatus(sourceCreatedAt) {
  if (!sourceCreatedAt) return { status: 'Active', severity: 'success' };
  const timeZone = getUserTimezone();
  const now = dayjs();
  const start = dayjs.utc(sourceCreatedAt.start).tz(timeZone);
  const end = dayjs.utc(sourceCreatedAt.end).tz(timeZone);
  return now.isBetween(start, end)
    ? { status: 'Active', severity: 'success' }
    : { status: 'Inactive', severity: 'statusNeutral' };
}

/**
 * Return a number of creator deliverables have been published without over reporting from one of the deliverable channels.
 * @param {object} creatorDeliverables - The deliverables object.
 */
export function getDeliverablesPublished(creatorDeliverables) {
  const platforms = [
    { published: 'publishedInstagramFeed', deliverable: 'deliverableInstagramFeed' },
    { published: 'publishedInstagramReels', deliverable: 'deliverableInstagramReels' },
    { published: 'publishedInstagramStories', deliverable: 'deliverableInstagramStories' },
    { published: 'publishedTiktokVideos', deliverable: 'deliverableTiktokVideos' },
    { published: 'publishedYoutubeVideos', deliverable: 'deliverableYoutubeVideos' },
    { published: 'publishedYoutubeShorts', deliverable: 'deliverableYoutubeShorts' },
  ];

  const deliverablesPublished = platforms.reduce(
    (total, platform) =>
      total +
      Math.min(creatorDeliverables[platform.published], creatorDeliverables[platform.deliverable]),
    0,
  );

  const allDeliverablesCompleted = platforms.every(
    (platform) =>
      creatorDeliverables[platform.published] >= creatorDeliverables[platform.deliverable],
  );

  return allDeliverablesCompleted ? creatorDeliverables.totalPublishedPosts : deliverablesPublished;
}

/**
 * Return an array of metrics for use on creator campaign cards
 * @param {object} campaign - The campaign object.
 */
export function getCreatorCampaignMetricItems(campaign) {
  const creatorDeliverables = campaign.creator_deliverables ?? {};
  let creatorDeliverablesStatus;
  if (creatorDeliverables.deliverableGifted) {
    creatorDeliverablesStatus = 'Gifted';
  } else if (creatorDeliverables.totalDeliverables) {
    const published = getDeliverablesPublished(creatorDeliverables);
    const total = creatorDeliverables.totalDeliverables ?? 0;
    creatorDeliverablesStatus = `${published}/${total}`;
  } else {
    creatorDeliverablesStatus = '-';
  }

  const dateRange = getCreatorsDateRange(campaign.creator_filters?.source_created_at);

  return [
    { label: 'Deliverables', value: creatorDeliverablesStatus },
    { label: 'Date', value: dateRange },
  ];
}

/**
 * Return formatted label for a point date on a graph.
 * @param {import("dayjs").Dayjs} pointDate
 * @param {{ scale: string; startDate: object; endDate: object }} options
 * @returns {string}
 */
export function formatGraphPointDate(pointDate, { scale, startDate, endDate }) {
  if (scale === GRAPH_SCALES.MONTHLY.value) {
    const isFirstMonth = pointDate.isSame(startDate, 'month');
    const isLastMonth = pointDate.isSame(endDate, 'month');

    // The goal is to display a data point like: "Jan 1-31"
    // If the date falls on the bounds of our date range, we need to show the minimum or maximum day based on the date range
    // So for a range Jan 17 - Feb 5, the first data point would have a label "Jan 17-31" and the last
    // data point would have a label of "Feb 1-5"
    const startDay = isFirstMonth ? startDate.date() : 1;
    const endDay = isLastMonth ? endDate.date() : pointDate.endOf('month').date();

    const formattedDate = `${pointDate.format('MMM')} ${startDay} - ${endDay}`;

    // We'll conditionally add the year to the data point if it falls outside the current year
    const isCurrentYear = pointDate.isSame(dayjs(), 'year');
    return !isCurrentYear ? `${formattedDate}, ${pointDate.format('YYYY')}` : formattedDate;
  }

  if (!dayjs().isSame(pointDate, 'year')) {
    return pointDate.format('MMM DD, YYYY');
  }
  return pointDate.format('MMM DD');
}

/**
 * Determines whether a campaign has media for a data source.
 * @param campaign - The campaign object.
 * @param source - The data source to check for.
 * @return True if the campaign has media for the data source, false otherwise.
 */
export function campaignHasDataSource(campaign, source) {
  return (
    (source === DATA_SOURCES.UGC && campaign.has_ugc) ||
    (source === DATA_SOURCES.RELATIONSHIPS && campaign.has_relationships) ||
    (source === DATA_SOURCES.LIKESHOP &&
      campaign.included_media_sources?.includes(CHANNELS.INSTAGRAM)) ||
    (source === DATA_SOURCES.TIKTOK_LIKESHOP &&
      campaign.included_media_sources?.includes(CHANNELS.TIKTOK)) ||
    (source === DATA_SOURCES.INSTAGRAM_CREATORS && campaign.creator_ids?.length > 0) ||
    (source === DATA_SOURCES.TIKTOK_CREATORS && campaign.creator_ids?.length > 0) ||
    campaign.included_media_sources?.includes(source)
  );
}

export function getMetricFormatOptions(format, { currency } = {}) {
  const options = {};

  if (FRACTIONAL_METRIC_TYPES.includes(format)) {
    options.minimumFractionDigits = 2;
    options.maximumFractionDigits = 2;
  }

  const formatType = METRIC_TYPE_TO_DASHING_UI_FORMAT[format];
  // When the currency is unknown, format options must omit the property to use fallback.
  if (formatType === Formatters.FORMAT_TYPES.CURRENCY && currency) {
    options.style = 'currency';
    options.currency = currency;
  }

  return options;
}

export function getDefaultUtmCampaign(campaign, creator) {
  const campaignName = campaign?.name?.trim();
  const creatorHandle = getHandle(creator);
  const hasValidHandle = creatorHandle && creatorHandle !== 'Handle Unavailable';

  if (campaignName && hasValidHandle) {
    return `${campaignName}-${creatorHandle}`;
  }
  if (campaignName) {
    return campaignName;
  }
  if (hasValidHandle) {
    return creatorHandle;
  }
  return '';
}

/**
 * Determines what types of content a campaign contains.
 * This function ONLY checks for content presence - it does NOT check permissions.
 *
 * @param {Object|Ref<Object>} campaign - Campaign object or Vue ref containing campaign data
 * @returns {Object} Campaign content status with boolean flags for different content types
 */
export function getCampaignContentStatus(campaign) {
  const campaignValue = toValue(campaign);

  if (isEmpty(campaignValue)) {
    return {
      hasMediaSources: false,
      hasUGC: false,
      hasOwned: false,
      hasAds: false,
      hasCreators: false,
      hasRelationships: false,
      hasGoogleAnalytics: false,
      isEmpty: true,
    };
  }

  const hasMediaSources = Boolean(campaignValue?.included_media_sources?.length);
  const hasUGC = Boolean(campaignValue?.has_ugc);
  const hasOwned = hasMediaSources || hasUGC;
  const hasAds = Boolean(campaignValue?.included_ad_sources?.length);
  const hasCreators = Boolean(campaignValue?.creator_ids?.length);
  const hasRelationships = Boolean(campaignValue?.has_relationships);
  const hasGoogleAnalytics = Boolean(campaignValue?.google_analytics_selected_campaigns?.length);
  const isCampaignEmpty = !hasOwned && !hasCreators && !hasRelationships && !hasAds;

  return {
    hasMediaSources,
    hasUGC,
    hasOwned,
    hasAds,
    hasCreators,
    hasRelationships,
    hasGoogleAnalytics,
    isEmpty: isCampaignEmpty,
  };
}
